import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';

import {
  TinyMceService,
} from 'webcommon/common-api';
import {
  BridgeService,
  DeviceSettingsRepository,
  ServerInfoRepository,
  WebCommonStorageRepository,
} from 'webcommon/legacy-common';
import {
  AngularHomepageSettingsDto,
  AppointmentListSettings,
  AppointmentsBadgeSettings,
  GuidStrValue,
  LicensesDto,
  MessageFilterDto,
  MessageSettingsDto,
  MessagesListSettings,
  PatientDashboardConfigurationV2Dto,
  PatientDashboardDeviceConfigurationDto,
  ProviderDashboardSettingsDto,
  ProviderDashboardTileTypes,
  ProviderDeviceSettingsDto,
  ScheduleSettingsDto,
  ServerFeature,
  SigWriterSettingsDto,
  TileV2Dto,
  UpdatePatientDashboardConfigurationV2Command,
  UserSettingsClinicalNoteTypeDto,
  UserSettingsCustomNotePrintsDto,
  UserSettingsDesktopAddInsDto,
  UserSettingsFhirClientAddInsDto,
  UserSettingsFndConfigurationDto,
} from 'webcommon/shared';
import {
  SettingRepository,
} from 'webcommon/webapi';

import { DashboardStructureService } from './DashboardStructureService';
import { ProviderSettingsDtoMapperService } from './ProviderSettingsDtoMapperService';

export interface GeneralNoteTypeSettings {
  PhoneMessageGeneralNoteTypeId?: string | null;
  TaskMessageGeneralNoteTypeId?: string | null;
  PortalMessageGeneralNoteTypeId?: string | null;
  DefaultVitalsSettingId?: string | null;
  RxRefillMessageGeneralNoteTypeId?: string | null;
  PharmacyGeneralNoteTypeId?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  static readonly ajsFactoryName = 'SettingServiceTs';

  private readonly setSettingSubject$ = new Subject<void>();
  private saveSettingsPromise = Promise.resolve();

  constructor(
    private bridgeService: BridgeService,
    private dashboardStructureService: DashboardStructureService,
    private deviceSettingsRepository: DeviceSettingsRepository,
    private providerSettingsDtoMapper: ProviderSettingsDtoMapperService,
    private serverInfoRepository: ServerInfoRepository,
    private settingRepository: SettingRepository,
    private tinyMceService: TinyMceService,
    private webCommonStorageRepository: WebCommonStorageRepository,
  ) {
    this.setSettingSubject$.asObservable().pipe(
      debounceTime(10000),
    ).subscribe(() => {
      this.saveSettingsPromise = this.saveSettingsPromise.finally(() => {
        return this.saveSettings().toPromise();
      });
    });
  }

  private setSetting(): void {
    if (this.serverAllowsSavingSettings()) {
      this.setSettingSubject$.next();
    }
  }

  private saveSettings() {
    const userId = this.webCommonStorageRepository.getUserIdOrThrow();
    const settings = this.providerSettingsDtoMapper.getProviderSettingsDto();
    return this.settingRepository.updateProviderSettings(userId, settings);
  }

  private getDeviceSettings(): ProviderDeviceSettingsDto | null {
    const providerSettings = this.webCommonStorageRepository.getProviderSettings();
    if (providerSettings) {
      if (this.deviceSettingsRepository.isPhone) {
        if (providerSettings.PhoneSettings) {
          return providerSettings.PhoneSettings;
        }
      }
      else if (this.deviceSettingsRepository.isTablet) {
        if (providerSettings.TabletSettings) {
          return providerSettings.TabletSettings;
        }
      } else {
        if (providerSettings.DesktopSettings) {
          return providerSettings.DesktopSettings;
        }
      }
    }

    return null;
  }

  private validateConfiguration(): ProviderDeviceSettingsDto {
    const deviceSettings = this.getDeviceSettings();
    if (!deviceSettings) {
      throw new Error('UserPRM settings are not available');
    }

    return deviceSettings;
  }

  getAngularHomepageSettings(): AngularHomepageSettingsDto | null | undefined {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.AngularHomepageSettings;
  }

  getClinicalNoteTypes(): UserSettingsClinicalNoteTypeDto[] {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.ClinicalNoteTypes;
  }

  // Gets the list of items that are allowed to be created 'New',
  // based on User Settings => Clinical Note Types tab => table checkboxes.
  getClinicalNoteTypes_Create(): UserSettingsClinicalNoteTypeDto[] {
    return this.getClinicalNoteTypes().filter((value) => {
      // include the items that are undefined or true, because we don't want to exclude them all
      // if this property isn't filled in for this version of PRM
      return value.IncludeInCreate !== false;
    });
  }

  getCustomNotePrints(): UserSettingsCustomNotePrintsDto {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.CustomNotePrints;
  }

  getDesktopAddIns(): UserSettingsDesktopAddInsDto[] {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.DesktopAddIns;
  }

  getFhirClientAddIns(): UserSettingsFhirClientAddInsDto[] {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.FhirClientAddIns;
  }

  getFndConfigurations(): UserSettingsFndConfigurationDto[] {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.FndConfigurations;
  }

  getLicenses(): LicensesDto {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.Licenses;
  }

  isDisplaySBStartStopTimes(): boolean {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.FncSettings.DisplaySBStartStopTimes;
  }

  isQuickSearchAllowed(): boolean {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.Security?.IsQuickSearchAllowed ?? false;
  }

  isConfigureHomeAllowed(): boolean {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.Security?.IsConfigureHomeAllowed ?? false;
  }

  isCacheClient(): boolean {
    return this.webCommonStorageRepository.getIsCacheClient();
  }

  isCacheClientOrUser(): boolean {
    return (this.webCommonStorageRepository.getIsCacheClient() || this.webCommonStorageRepository.getIsCacheUser());
  }

  isModifyCurrentSettingsAllowed(): boolean {
    const deviceSettings = this.validateConfiguration();
    return !!deviceSettings.UserSettingId && (deviceSettings.Security?.IsModifyCurrentSettingsAllowed ?? false);
  }

  isDemographicsAllowed(): boolean {
    const deviceSettings = this.validateConfiguration();
    return deviceSettings.Security?.IsDemographicsAllowed ?? false;
  }

  private serverAllowsSavingSettings(): boolean {
    return this.serverInfoRepository.isFeatureSupported('SaveSettings');
  }

  async loadSettings(): Promise<void> {
    this.webCommonStorageRepository.setDefaultAppointmentsBadgeSettings({
      hideDischarged: true
    });

    await forkJoin([
      this.loadPatientDashboardTiles(),
      this.loadPatientDashboardConfigurations(),
      this.loadProviderSettings(),
      this.loadSiteSettings(),
    ]).toPromise();
  }

  private loadPatientDashboardTiles() {
    return this.settingRepository.getPatientDashboardTiles().pipe(
      tap(list => {
        this.webCommonStorageRepository.removePatientDashboardWidgets();
        this.webCommonStorageRepository.setPatientDashboardWidgets(list);
      }),
    );
  }

  private patientDashboardConfigurations() {
    return this.dashboardStructureService.get().pipe(
      map(configs => {
        const result = {
          phoneConfigs: configs.map(config => this.mapToDeviceConfig(config, config.PhoneTiles)),
          tabletConfigs: configs.map(config => this.mapToDeviceConfig(config, config.TabletTiles)),
          desktopConfigs: configs.map(config => this.mapToDeviceConfig(config, config.DesktopTiles)),
          allConfigs: configs,
        };

        return result;
      }),
    );
  }

  private mapToDeviceConfig(config: PatientDashboardConfigurationV2Dto, tiles: TileV2Dto[]): PatientDashboardDeviceConfigurationDto {
    return {
      Id: config.Id,
      ExternalId: config.ExternalId,
      Name: config.Name,
      DisplayOrder: config.DisplayOrder,
      Tiles: cloneDeep(tiles),
    };
  }

  private loadPatientDashboardConfigurations() {
    return this.patientDashboardConfigurations().pipe(
      tap(config => {
        this.webCommonStorageRepository.setPatientDashboardConfigurations(config.phoneConfigs, 1);
        this.webCommonStorageRepository.setPatientDashboardConfigurations(config.tabletConfigs, 2);
        this.webCommonStorageRepository.setPatientDashboardConfigurations(config.desktopConfigs, 3);
        this.webCommonStorageRepository.setPatientDashboardConfigurations(config.allConfigs, 'all');
      }, () => {
        console.error('error getting patient dashboard configurations');
      }),
    );
  }

  private loadProviderSettings() {
    const userId = this.webCommonStorageRepository.getUserIdOrThrow();

    if (this.serverAllowsSavingSettings()) {
      return this.settingRepository.getProviderSettings(userId).pipe(
        tap(providerSettings => {
          let deviceSettings: ProviderDeviceSettingsDto | null = null;

          if (this.deviceSettingsRepository.isPhone) {
            deviceSettings = providerSettings.PhoneSettings;
          } else if (this.deviceSettingsRepository.isTablet) {
            deviceSettings = providerSettings.TabletSettings;
          } else {
            deviceSettings = providerSettings.DesktopSettings;
          }

          if (!deviceSettings) {
            deviceSettings = providerSettings.PhoneSettings ||
                             providerSettings.TabletSettings ||
                             providerSettings.DesktopSettings;
          }

          if (!deviceSettings) {
            return;
          }

          this.setDashboardSettings(deviceSettings.DashboardSettings, deviceSettings);
          this.setGeneralNoteTypeSettings(deviceSettings.GeneralNoteTypes);
          this.webCommonStorageRepository.setCurrentUserLicenses(deviceSettings.Licenses);
          this.webCommonStorageRepository.setIsERxEnabled(deviceSettings.IsERxEnabled);
          this.webCommonStorageRepository.setAllowEPCSGoldLAC(deviceSettings.AllowEPCSGoldLAC);
          this.webCommonStorageRepository.setAllowEPCSGoldPOB(deviceSettings.AllowEPCSGoldPOB);
          this.webCommonStorageRepository.setAllowImperialUnits(deviceSettings.AllowImperialUnitsInPrescription);
          const superbillMethod = deviceSettings.DefaultSuperbillStatusId || 0;
          this.webCommonStorageRepository.setDefaultSuperbillSubmissionMethod(superbillMethod);
          this.webCommonStorageRepository.setProviderSettings(providerSettings);
          this.webCommonStorageRepository.setIsCacheClient(deviceSettings.IsCacheClient);
        }),
      );
    } else {
      const dashboardRequest = this.settingRepository.getProviderDashboard(userId).pipe(
        tap(dashboardSettings => {
          this.setDashboardSettings(dashboardSettings);
        }),
      );

      const userSettingsRequest = this.settingRepository.getUserSettings(userId).pipe(
        tap(userSettings => {
          this.setGeneralNoteTypeSettings(userSettings);
          this.webCommonStorageRepository.setCurrentUserLicenses(null);
          this.webCommonStorageRepository.setAllowEPCSGoldLAC(userSettings.AllowEPCSGoldLAC || false);
          this.webCommonStorageRepository.setAllowEPCSGoldPOB(userSettings.AllowEPCSGoldPOB || false);
          this.webCommonStorageRepository.setAllowImperialUnits(userSettings.AllowImperialUnitsInPrescription || false);
          this.webCommonStorageRepository.setIsCacheClient(userSettings.IsCacheClient || false);
        }),
      );

      return forkJoin([ dashboardRequest, userSettingsRequest ]);
    }
  }

  private loadSiteSettings() {
    if (!this.serverInfoRepository.isSupported(ServerFeature.Settings_SiteSettings)) {
      return of(null);
    }

    return this.settingRepository.getSiteSettings().pipe(
      switchMap(siteSettings => {
        this.webCommonStorageRepository.setSiteSettings(siteSettings);
        return from(this.tinyMceService.load(siteSettings.CustomerLicenseAssets));
      }),
    );
  }

  updateNativeSettings(): void {
    const settings = [];

    const badgeSetting = {
      Name: 'BadgeAppointmentsHideDischarged',
      Value: this.webCommonStorageRepository.getAppointmentsBadgeSettings()?.hideDischarged,
    };

    const calendarSettings = this.webCommonStorageRepository.getAppointmentsListSettings();
    const currentCalendar = calendarSettings &&
      calendarSettings.calendarIds &&
      calendarSettings.calendarIds[calendarSettings.index];
    const calendarUidSetting = {
      Name: 'BadgeAppointmentsCalendarUid',
      Value: currentCalendar,
    };

    const messageListSettings = this.webCommonStorageRepository.getMessagesListSettings();
    const messageFilter = messageListSettings &&
      messageListSettings.filters &&
      messageListSettings.filters[messageListSettings.startFilter];
    const messageFilterUidSetting = {
      Name: 'BadgeMessageFilterUid',
      Value: messageFilter && messageFilter.Id,
    };

    settings.push(badgeSetting);
    settings.push(calendarUidSetting);
    settings.push(messageFilterUidSetting);
    console.log('badge settings', settings);
    this.bridgeService.fireEvent('updateSettings', { settings });
  }

  private setDashboardSettings(dashboardSettings: ProviderDashboardSettingsDto, providerSettings?: ProviderDeviceSettingsDto): void {
    this.storeDashboardTileSettings(dashboardSettings);
    this.storeCalendarSettings(dashboardSettings, providerSettings?.ScheduleSettings);
    this.storeMessageSettings(dashboardSettings, providerSettings?.MessageSettings);

    this.updateNativeSettings();
  }

  private setGeneralNoteTypeSettings(generalNoteTypes: GeneralNoteTypeSettings) {
    this.webCommonStorageRepository.setPhoneMessageGeneralNoteTypeId(
      generalNoteTypes.PhoneMessageGeneralNoteTypeId || '10101010-0000-0000-0000-000000000003'
    );
    this.webCommonStorageRepository.setTaskMessageGeneralNoteTypeId(
      generalNoteTypes.TaskMessageGeneralNoteTypeId || '10101010-0000-0000-0000-000000000005'
    );
    this.webCommonStorageRepository.setPortalMessageGeneralNoteTypeId(
      generalNoteTypes.PortalMessageGeneralNoteTypeId || '5E9280E5-3388-49E8-A5AE-4DFFBB0A9B89'
    );
    this.webCommonStorageRepository.setPatientVitalsDefaultSettingId(generalNoteTypes.DefaultVitalsSettingId || null);
    const rxRefillMessageGeneralNoteTypeId = generalNoteTypes.RxRefillMessageGeneralNoteTypeId || 'A2B88BC2-E7C8-4990-92F6-AE28B5C0DC77';
    this.webCommonStorageRepository.setRxRefillMessageGeneralNoteTypeId(rxRefillMessageGeneralNoteTypeId);
    // Reuse for now but store separately.
    this.webCommonStorageRepository.setPharmacyRefillMessageGeneralNoteTypeId(rxRefillMessageGeneralNoteTypeId);
    this.webCommonStorageRepository.setPharmacyGeneralNoteTypeId(
      generalNoteTypes.PharmacyGeneralNoteTypeId || '10101010-0000-0000-0000-000000000010'
    );
  }

  private storeDashboardTileSettings(dashboardSettings: ProviderDashboardSettingsDto) {
    // TODO: use proper typing
    const tiles = dashboardSettings.Tiles as any[];

    for (const tile of tiles) {
      if (tile.Calendars) {
        tile.Calendar = tile.Calendars.length ? tile.Calendars[0] : null;
      }
      else if (tile.Calendar) {
        tile.Calendars = [tile.Calendar];
      }

      const serviceSite = tile.ExternalPatientsFilter?.ServiceSite;
      if (serviceSite && !this.webCommonStorageRepository.getDefaultServiceSiteId()) {
        this.webCommonStorageRepository.setDefaultServiceSiteId(serviceSite.Id);
      }
    }

    this.webCommonStorageRepository.setDefaultDashboardTileSettings({
      Tiles: tiles,
    });
  }

  private storeCalendarSettings(dashboardSettings: ProviderDashboardSettingsDto, scheduleSettings?: ScheduleSettingsDto) {
    let setting: AppointmentListSettings | undefined;

    if (scheduleSettings) {
      setting = {
          calendarIds: scheduleSettings.ScheduleCalendarIds?.length ? scheduleSettings.ScheduleCalendarIds : [],
          displayEndTime: !!scheduleSettings.DisplayEndTime,
          findSlotForNewAppt: !!scheduleSettings.FindSlotForNewAppt,
          index: 0,
      };
    } else {
      let calendarIds: string[] = [];
      for (const tile of dashboardSettings.Tiles) {
        if (tile.Type === ProviderDashboardTileTypes.Calendar) {
          if (tile.Calendars?.length) {
            calendarIds = tile.Calendars.map(x => x.Id);
          }
          break;
        }
      }

      setting = {
        calendarIds,
        displayEndTime: false,
        findSlotForNewAppt: false,
        index: 0,
      };
    }

    this.webCommonStorageRepository.setDefaultAppointmentsListSettings(setting);
  }

  private storeMessageSettings(dashboardSettings: ProviderDashboardSettingsDto, messageSettings?: MessageSettingsDto) {
    let messageFilters: MessageFilterDto[] = [];

    if (messageSettings) {
      messageFilters = messageSettings.MessageFilterIds.map(id => {
        return {
          Id: id,
          Description: null,
          ExternalId: null,
        };
      });
    }

    if (!messageFilters.length) {
      for (const tile of dashboardSettings.Tiles) {
        if (tile.Type === ProviderDashboardTileTypes.Messages && tile.MessageFilter) {
          messageFilters.push(tile.MessageFilter);
        }
      }
    }

    this.webCommonStorageRepository.setDefaultMessagesListSettings({
      filters: messageFilters,
      startFilter: 0
    });
  }

  updatePatientDashboardConfiguration(
    dashboardConfiguration: UpdatePatientDashboardConfigurationV2Command,
  ): Promise<PatientDashboardConfigurationV2Dto> {
    if (!dashboardConfiguration.isNewDashboard) {
      return this.settingRepository.updatePatientDashboardConfiguration(dashboardConfiguration).toPromise();
    } else {
      return this.settingRepository.createPatientDashboardConfiguration(dashboardConfiguration).toPromise();
    }
  }

  async deletePatientDashboardConfiguration(dashboardConfiguration: UpdatePatientDashboardConfigurationV2Command): Promise<void> {
    if (!dashboardConfiguration.isNewDashboard) {
      await this.settingRepository.deletePatientDashboardConfiguration(dashboardConfiguration.Id).toPromise();
    }
  }

  getSigWriterSettings(): Observable<SigWriterSettingsDto> {
    if (!this.serverInfoRepository.isFeatureSupported('Sigwriter_ListSettings')) {
      // if the endpoint isn't available, then return a default value.
      // This data structure should match the structure of the object returned from the endpoint below.
      return of({
        FrequencyDisplayAbbreviationFirst: false,
        IsDxInPharmacyInstructionsAnError: true,
      });
    }

    /*
    getSigWriterSettings() is intended to be called from SigwriterRxService.loadOptions().
    And, by design, meant to call the respective end point (SettingRepository.getSigWriterSettings)
    to retrieve the Sig Writer List Settings each time the Sig Writer is opened (called by loadOptions).
    By doing this, mobile users will not be required to reopen the mobile application to in order for
    any behavior to be updates.

    If, when, the PRM applications become completely web based (not hybrid), this caching
    implementation will need to be revisited.
    */
    return this.settingRepository.getSigWriterSettings();
  }

  imperialUnitsAllowed(): boolean {
    return this.webCommonStorageRepository.getAllowImperialUnits();
  }

  getPatientDashboardWidgets() {
    return this.webCommonStorageRepository.getPatientDashboardWidgets();
  }

  getAppointmentsListSettings() {
    return this.webCommonStorageRepository.getAppointmentsListSettings();
  }

  setAppointmentsListSettings(settings: AppointmentListSettings): void {
    this.webCommonStorageRepository.setAppointmentsListSettings(settings);
    this.setSetting();
  }

  getMessagesListSettings() {
    return this.webCommonStorageRepository.getMessagesListSettings();
  }

  setMessagesListSettings(settings: MessagesListSettings): void {
    this.webCommonStorageRepository.setMessagesListSettings(settings);
    this.setSetting();
  }

  getAppointmentsBadgeSettings() {
    return this.webCommonStorageRepository.getAppointmentsBadgeSettings();
  }

  setAppointmentsBadgeSettings(settings: AppointmentsBadgeSettings): void {
    this.webCommonStorageRepository.setAppointmentsBadgeSettings(settings);
    this.setSetting();
  }

  getDashboardTileSettings() {
    return this.webCommonStorageRepository.getDashboardTileSettings();
  }

  setDashboardTileSettings(settings: ProviderDashboardSettingsDto): void {
    this.webCommonStorageRepository.setDashboardTileSettings(settings);
    this.setSetting();
  }

  // !userId means just use the CurrentUser
  async getKdbConfigurationId(userId?: string, renderingProviderId?: string, billingProviderId?: string): Promise<GuidStrValue | null> {
    const kdbConfiguration = await this.settingRepository.getKdbConfiguration(userId, renderingProviderId, billingProviderId).toPromise();
    if (!kdbConfiguration) {
      return null;
    }

    return kdbConfiguration.KdbConfigurationId;
  }

  // !userId means just use the CurrentUser
  async getKdbConfigurationIdByVisit(userId?: string, visitId?: string): Promise<GuidStrValue | null> {
    const kdbConfiguration = await this.settingRepository.getKdbConfigurationByVisit(userId, visitId).toPromise();
    if (!kdbConfiguration) {
      return null;
    }

    return kdbConfiguration.KdbConfigurationId;
  }

  async getDrugSearchIncludeInactive(providerId: string): Promise<boolean> {
    return await this.settingRepository.getDrugSearchIncludeInactive(providerId).toPromise();
  }
}
